import React, { useState, useEffect } from 'react';
import { api } from '../api';

function Debug() {
  const [testDbResult, setTestDbResult] = useState<any>(null);
  const [allInteractionsResult, setAllInteractionsResult] = useState<any>(null);
  const [systemDiagnostic, setSystemDiagnostic] = useState<any>(null);
  const [serverTime, setServerTime] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const runTests = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Test DB connection
      console.log('Testing database connection...');
      const testDbResponse = await api.testDatabaseConnection();
      setTestDbResult(testDbResponse);
      
      // Get all interactions
      console.log('Getting all interactions...');
      const allInteractionsResponse = await api.getAllInteractions();
      setAllInteractionsResult(allInteractionsResponse);
      
      // Get system diagnostic
      console.log('Getting system diagnostic...');
      const diagnosticResponse = await api.getSystemDiagnostic();
      setSystemDiagnostic(diagnosticResponse);
      
      // Get server time
      console.log('Getting server time...');
      const serverTimeResponse = await api.getServerTime();
      setServerTime(serverTimeResponse);
      
    } catch (err: any) {
      console.error('Debug test error:', err);
      setError(err.message || 'An unknown error occurred');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    runTests();
  }, []);

  return (
    <div className="container mx-auto p-4 bg-gray-100">
      <h1 className="text-2xl font-bold mb-4 text-gray-900">API Debug Page</h1>
      
      {loading && <p className="mb-4 text-blue-700 font-medium">Carregando...</p>}
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          <p><strong>Error:</strong> {error}</p>
        </div>
      )}
      
      <div className="grid grid-cols-1 gap-4 mb-4">
        <div className="bg-white p-4 rounded shadow border border-gray-200">
          <h2 className="text-lg font-semibold mb-2 text-gray-900">Server Time</h2>
          {serverTime ? (
            <div className="text-gray-800">
              <p>Current Time: <strong className="text-black">{serverTime.server_time?.formatted_time || 'N/A'}</strong></p>
              <p>Date: <strong className="text-black">{serverTime.server_time?.server_date || 'N/A'}</strong></p>
              <p>Hour: <strong className="text-black">{serverTime.server_time?.current_hour || 0}:{String(serverTime.server_time?.current_minute || 0).padStart(2, '0')}</strong></p>
              <p>Record Count: <strong className="text-black">{serverTime.record_count || 'N/A'}</strong></p>
              {serverTime.database_info && (
                <div className="mt-2">
                  <p>Total Records: <strong className="text-black">{serverTime.database_info.total_records}</strong></p>
                  <h3 className="text-md font-medium mt-2 text-gray-900">Recent Dates:</h3>
                  <ul className="list-disc pl-5 mt-1">
                    {serverTime.database_info.recent_dates.map((date: any, index: number) => (
                      <li key={index} className="text-gray-800">
                        {date.data}: <strong className="text-black">{date.total}</strong> interactions
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <p className="text-gray-800">No server time data available</p>
          )}
        </div>
      </div>
      
      <div className="grid grid-cols-1 gap-4 mb-4">
        <div className="bg-white p-4 rounded shadow border border-gray-200">
          <h2 className="text-lg font-semibold mb-2 text-gray-900">System Diagnostic</h2>
          {systemDiagnostic ? (
            <div className="text-gray-800">
              <p>Status: <strong className="text-black">{systemDiagnostic.success ? 'OK' : 'Error'}</strong></p>
              <p>Time: <strong className="text-black">{systemDiagnostic.timestamp}</strong></p>
              
              {systemDiagnostic.connection && (
                <div className="mt-2">
                  <h3 className="text-md font-medium text-gray-900">Connection:</h3>
                  <p>Status: <strong className={systemDiagnostic.connection.status === 'connected' ? 'text-green-600' : 'text-red-600'}>{systemDiagnostic.connection.status}</strong></p>
                  <p>Server Time: <strong className="text-black">{new Date(systemDiagnostic.connection.server_time).toLocaleString()}</strong></p>
                </div>
              )}
              
              {systemDiagnostic.database && (
                <div className="mt-2">
                  <h3 className="text-md font-medium text-gray-900">Database:</h3>
                  <p>Total Records: <strong className="text-black">{systemDiagnostic.database.total_records}</strong></p>
                  
                  {systemDiagnostic.database.server_date && (
                    <div className="mt-1">
                      <h4 className="text-sm font-medium text-gray-900">Server Date Info:</h4>
                      <p>Date: <strong className="text-black">{systemDiagnostic.database.server_date.data_atual}</strong></p>
                      <p>Timestamp: <strong className="text-black">{systemDiagnostic.database.server_date.timestamp_atual}</strong></p>
                      <p>Time: <strong className="text-black">{systemDiagnostic.database.server_date.hora_atual}</strong></p>
                    </div>
                  )}
                  
                  {systemDiagnostic.database.today_records && (
                    <div className="mt-1">
                      <h4 className="text-sm font-medium text-gray-900">Today's Records:</h4>
                      <p>Total: <strong className="text-black">{systemDiagnostic.database.today_records.total}</strong></p>
                      {systemDiagnostic.database.today_records.primeiro_registro && (
                        <p>First: <strong className="text-black">{new Date(systemDiagnostic.database.today_records.primeiro_registro).toLocaleString()}</strong></p>
                      )}
                      {systemDiagnostic.database.today_records.ultimo_registro && (
                        <p>Last: <strong className="text-black">{new Date(systemDiagnostic.database.today_records.ultimo_registro).toLocaleString()}</strong></p>
                      )}
                    </div>
                  )}
                  
                  <div className="mt-2">
                    <h4 className="text-sm font-medium text-gray-900">Records by Date:</h4>
                    <div className="max-h-60 overflow-y-auto mt-1">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Date</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Count</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">First</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Last</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {systemDiagnostic.database.records_by_date.map((record: any, index: number) => (
                            <tr key={index} className={record.data === systemDiagnostic.database.server_date.data_atual ? 'bg-blue-50' : ''}>
                              <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-800">{record.data}</td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-800">{record.total}</td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-800">{new Date(record.primeiro_registro).toLocaleTimeString()}</td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-800">{new Date(record.ultimo_registro).toLocaleTimeString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <p className="text-gray-800">No diagnostic data available</p>
          )}
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white p-4 rounded shadow border border-gray-200">
          <h2 className="text-lg font-semibold mb-2 text-gray-900">Database Connection Test</h2>
          {testDbResult ? (
            <pre className="bg-gray-100 p-2 rounded overflow-auto max-h-60 text-gray-900 text-sm">
              {JSON.stringify(testDbResult, null, 2)}
            </pre>
          ) : (
            <p className="text-gray-800">No result yet</p>
          )}
        </div>
        
        <div className="bg-white p-4 rounded shadow border border-gray-200">
          <h2 className="text-lg font-semibold mb-2 text-gray-900">All Interactions</h2>
          {allInteractionsResult ? (
            <pre className="bg-gray-100 p-2 rounded overflow-auto max-h-60 text-gray-900 text-sm">
              {JSON.stringify(allInteractionsResult, null, 2)}
            </pre>
          ) : (
            <p className="text-gray-800">No result yet</p>
          )}
        </div>
      </div>
      
      <button 
        onClick={runTests}
        className="mt-4 bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
        disabled={loading}
      >
        Run Tests Again
      </button>
    </div>
  );
}

export default Debug; 